import { useRef, useState } from "react";
import "./App.css";

const ADMIN_PATHS = ["/admin-upload", "/download-page/admin-upload"];
const APP_BASE_URL = process.env.REACT_APP_URL || window.location.origin;
const LOGIN_ENDPOINT = `${APP_BASE_URL}/houdix/seen/admin/login`;
const LOGO_URL = "https://www.seen-sy.com/App%20Logo%20(2).png";

const uploadConfig = {
  android: {
    label: "Android APK",
    helper: "Upload the latest Android build and publish it as seen.apk",
    endpoint: `${APP_BASE_URL}/houdix/seen/admin/apps/android`,
    accept: ".apk",
    activeName: "seen.apk",
  },
  ios: {
    label: "iOS IPA",
    helper: "Upload the latest iOS build and publish it as iseen.ipa",
    endpoint: `${APP_BASE_URL}/houdix/seen/admin/apps/ios`,
    accept: ".ipa",
    activeName: "iseen.ipa",
  },
};

const readCookie = (name) => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
};

const writeCookie = (name, value, maxAgeSeconds) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

function SeenLogo() {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return <div className="logo-fallback">S</div>;
  }

  return (
    <img
      src={LOGO_URL}
      alt="Seen Logo"
      onError={() => setBroken(true)}
    />
  );
}

function AdminLogin({ onLoginSuccess }) {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [tokenValue, setTokenValue] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCredentials((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(LOGIN_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.token) {
        throw new Error(result.message || "Login failed.");
      }

      writeCookie("token", result.token, 60 * 60 * 24);
      setStatus("success");
      setMessage("Logged in successfully. You can upload files now.");
      onLoginSuccess(result.token);
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "Login failed.");
    }
  };

  const handleTokenSubmit = (event) => {
    event.preventDefault();

    if (!tokenValue.trim()) {
      setStatus("error");
      setMessage("Paste a token first.");
      return;
    }

    writeCookie("token", tokenValue.trim(), 60 * 60 * 24);
    setStatus("success");
    setMessage("Token saved successfully. You can upload files now.");
    onLoginSuccess(tokenValue.trim());
  };

  return (
    <div className="login-panel">
      <form className="login-section" onSubmit={handleSubmit}>
        <div className="login-header">
          <p className="upload-eyebrow">Admin Access</p>
          <h3>Sign in to upload</h3>
          <p className="upload-helper">
            Use your admin email and password here on this domain so the page can upload app files.
          </p>
        </div>

        <label className="login-label" htmlFor="admin-email">Email</label>
        <input
          id="admin-email"
          className="login-input"
          type="email"
          name="email"
          value={credentials.email}
          onChange={handleChange}
          placeholder="admin@example.com"
          required
        />

        <label className="login-label" htmlFor="admin-password">Password</label>
        <input
          id="admin-password"
          className="login-input"
          type="password"
          name="password"
          value={credentials.password}
          onChange={handleChange}
          placeholder="Password"
          required
        />

        <button type="submit" className="upload-btn primary login-submit" disabled={status === "loading"}>
          {status === "loading" ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <div className="login-divider">or</div>

      <form className="login-section" onSubmit={handleTokenSubmit}>
        <div className="login-header">
          <p className="upload-eyebrow">Token Access</p>
          <h3>Use an existing token</h3>
          <p className="upload-helper">
            Helpful for local testing if your local database does not contain the admin account yet.
          </p>
        </div>

        <label className="login-label" htmlFor="admin-token">Bearer token</label>
        <textarea
          id="admin-token"
          className="login-input token-input"
          value={tokenValue}
          onChange={(event) => setTokenValue(event.target.value)}
          placeholder="Paste a valid admin JWT token"
          rows={4}
        />

        <button type="submit" className="upload-btn secondary login-submit">
          Use token
        </button>
      </form>

      {message ? <p className={`upload-message ${status === "success" ? "success" : "error"}`}>{message}</p> : null}
    </div>
  );
}

function UploadCard({ platform, config, token }) {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const triggerFileDialog = () => {
    inputRef.current?.click();
  };

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith(config.accept)) {
      setSelectedFile(null);
      setStatus("error");
      setMessage(`Please select a ${config.accept} file.`);
      resetInput();
      return;
    }

    setSelectedFile(file);
    setStatus("ready");
    setMessage(`${file.name} is ready to upload.`);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setStatus("error");
      setMessage("Choose a file before uploading.");
      return;
    }

    if (!token) {
      setStatus("error");
      setMessage("Please sign in first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setStatus("uploading");
    setMessage("Uploading now...");

    try {
      const response = await fetch(config.endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "Upload failed.");
      }

      setStatus("success");
      setMessage(
        result.archivedFile
          ? `Uploaded successfully. Previous file archived as ${result.archivedFile}.`
          : `Uploaded successfully. ${config.activeName} is now live.`
      );
      setSelectedFile(null);
      resetInput();
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "Upload failed.");
    }
  };

  return (
    <div className={`upload-card ${status}`}>
      <div className="upload-card-head">
        <div>
          <p className="upload-eyebrow">{platform === "android" ? "Android" : "iOS"}</p>
          <h3>{config.label}</h3>
          <p className="upload-helper">{config.helper}</p>
        </div>
        {status === "success" ? <div className="success-badge">✓</div> : null}
      </div>

      <div className="upload-actions">
        <button type="button" className="upload-btn secondary" onClick={triggerFileDialog}>
          Choose file
        </button>
        <button
          type="button"
          className="upload-btn primary"
          onClick={handleUpload}
          disabled={status === "uploading"}
        >
          {status === "uploading" ? "Uploading..." : "Upload"}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={config.accept}
        className="file-input"
        onChange={handleFileChange}
        aria-label={`${config.label} file input`}
      />

      <div className="upload-meta">
        <span className="target-name">Active file: {config.activeName}</span>
        <span className="selected-name">{selectedFile ? selectedFile.name : "No file selected"}</span>
      </div>

      {message ? <p className={`upload-message ${status}`}>{message}</p> : null}
    </div>
  );
}

function UploadPage() {
  const [token, setToken] = useState(() => readCookie("token"));

  return (
    <div className="main admin-main">
      <img src="https://www.seen-sy.com/(O).png" className="bg-image" alt="background" />

      <div className="header-section admin-header">
        <div className="logo-container">
          <SeenLogo />
        </div>
        <h1 className="admin-title">Seen App Upload Center</h1>
        <h2 className="subtitle">Manage the live Android and iOS app files</h2>
      </div>

      {!token ? <AdminLogin onLoginSuccess={setToken} /> : null}

      <div className="upload-grid">
        <UploadCard platform="android" config={uploadConfig.android} token={token} />
        <UploadCard platform="ios" config={uploadConfig.ios} token={token} />
      </div>
    </div>
  );
}

function LandingPage() {
  const googlePlayLink = "https://play.google.com/store/apps/details?id=com.seenapp.seen";
  const telegramLink = "https://t.me/seen_app_sy/126";
  const directDownloadLink = "https://www.seen-sy.com/houdix/seen/app/download";
  const supportLink = "https://wa.me/message/XPZ5PK4YGWWPF1";

  const trackClick = (buttonName) => {
    console.log("Clicked:", buttonName);
  };

  const handleAppStoreClick = (e) => {
    e.preventDefault();
    alert("تطبيقنا غير متوفر بعد على App Store، لكنه سيكون متاحاً قريباً");
    trackClick("app_store");
  };

  return (
    <div className="main">
      <img src="https://www.seen-sy.com/(O).png" className="bg-image" alt="background" />
      <div className="header-section">
        <div className="logo-container">
          <SeenLogo />
        </div>

        <h2 className="subtitle">مفتاح الحل بإيدك</h2>
      </div>

      <div className="buttons-container">
        <a
          href={googlePlayLink}
          className="download-btn"
          onClick={() => trackClick("googlePlay")}
        >
          <div className="btn-icon">
            <img src="https://www.seen-sy.com/googleplay.svg" alt="google play" />
          </div>

          <div className="btn-text">
            <span className="small-text">تحميل من</span>
            <span className="large-text">Google Play</span>
          </div>
        </a>

        <a href="/" className="download-btn" onClick={handleAppStoreClick}>
          <div className="btn-icon">
            <img src="https://www.seen-sy.com/appstore.svg" alt="app store" />
          </div>

          <div className="btn-text">
            <span className="small-text">تحميل من</span>
            <span className="large-text">App Store</span>
          </div>
        </a>

        <a
          href={telegramLink}
          className="download-btn"
          onClick={() => trackClick("telegram")}
        >
          <div className="btn-icon">
            <img src="https://www.seen-sy.com/telegram.svg" alt="telegram" />
          </div>

          <div className="btn-text">
            <span className="small-text">تحميل من</span>
            <span className="large-text">Telegram</span>
          </div>
        </a>

        <a
          href={directDownloadLink}
          className="download-btn"
          onClick={() => trackClick("direct_download")}
        >
          <div className="btn-icon">
            <img src="https://www.seen-sy.com/download.svg" alt="download" />
          </div>

          <div className="btn-text">
            <span className="large-text">تحميل مباشر</span>
          </div>
        </a>
      </div>

      <a
        href={supportLink}
        className="support-link"
        onClick={() => trackClick("support")}
      >
        هل تواجه مشكلة؟ <span className="highlight">تواصل معنا</span>
      </a>
    </div>
  );
}

function App() {
  const currentPath = window.location.pathname;
  const isAdminPage = ADMIN_PATHS.some((adminPath) => currentPath.endsWith(adminPath));

  return isAdminPage ? <UploadPage /> : <LandingPage />;
}

export default App;
